import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  // Base
  [
    'inline-flex items-center justify-center gap-2',
    'whitespace-nowrap font-semibold',
    'rounded-[10px] transition-all duration-150',
    'focus-visible:ring-primary-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'cursor-pointer select-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-primary-500 hover:bg-primary-600 text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0',
        accent:
          'bg-accent-400 text-ink-900 hover:bg-accent-500 shadow-md hover:-translate-y-0.5 active:translate-y-0',
        gold: 'bg-gradient-to-b from-[#f7d068] to-[#f4c64e] text-[#0a2e57] shadow-[0_4px_0_#c79a26,0_4px_16px_-2px_rgba(199,154,38,0.35)] hover:from-[#f4c64e] hover:to-[#e7b431] hover:shadow-[0_4px_0_#c79a26,0_12px_36px_-4px_rgba(199,154,38,0.4)] active:translate-y-0.5 active:shadow-[0_1px_0_#c79a26]',
        secondary: 'bg-ink-100 text-ink-900 hover:bg-ink-200 active:bg-ink-300',
        outline:
          'text-ink-900 border-ink-200 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 border bg-transparent',
        ghost: 'text-primary-600 hover:bg-primary-50 hover:text-primary-700 bg-transparent',
        destructive:
          'bg-error-500 hover:bg-error-600 text-white shadow-md hover:-translate-y-0.5 active:translate-y-0',
        link: 'text-primary-600 h-auto p-0 underline-offset-4 shadow-none hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-[15px]',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, fullWidth, loading, disabled, asChild, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Slot (asChild) exige React.Children.only → on ne peut pas lui passer
    // [spinner, children]. Quand on est en asChild, on passe juste le children
    // (le spinner n'a pas de sens sur un <Link>, il est utile uniquement sur
    // un vrai <button> qui submit ou trigger une action).
    const content = asChild ? (
      children
    ) : (
      <>
        {loading && (
          <span
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        )}
        {children}
      </>
    );

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        // Exposé pour le ciblage CSS thématique (cf. apps/app .theme-ocean
        // [data-variant='primary'] → pilule dorée). Sans .theme-ocean (marketing),
        // l'attribut est inerte.
        data-variant={variant ?? 'primary'}
        // `disabled` ne s'applique qu'à un vrai <button> ; sur asChild on s'appuie sur le styling
        {...(asChild ? {} : { disabled: disabled ?? loading })}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { buttonVariants };
